import { useState, useMemo } from 'react';
import { DataTableColumn } from 'mantine-datatable';
import { BaseRecord } from '@refinedev/core';
import { Table, TableProps } from './Table';
import { useTable, TableMode } from './hooks/useTable';

type SimpleTableProps<T extends BaseRecord> = {
  resource: string;
  mode?: TableMode;
  columns: DataTableColumn<T>[];
  filters?: Record<string, string>;
  searchAttributes?: string[];
  pageSize?: number;
  onRowClick?: (record: T) => void;
  title?: string;
  titleIcon?: React.ReactNode;
  onTitleClick?: () => void;
  primaryButton?: {
    label: string;
    onClick: () => void;
    variant?: 'default' | 'primary';
  };
  HeaderRow?: React.ReactNode;
  showCount?: boolean;
  enableSearch?: boolean;
  tableProps?: Partial<
    Omit<TableProps<T>, 'data' | 'isLoading' | 'pagination' | 'totalRecords' | 'columns'>
  >;
  filterPredicate?: (item: T, search: string) => boolean;
  // Reset pagination when this changes
  paginationDependencies?: unknown;
  // Use local state for pagination instead of URL params (useful for multi-table pages)
  useLocalPagination?: boolean;
};

export function SimpleTable<T extends BaseRecord>({
  resource,
  mode = 'client',
  columns,
  filters,
  searchAttributes,
  pageSize = 10,
  showCount = false,
  onRowClick,
  title,
  titleIcon,
  onTitleClick,
  primaryButton,
  HeaderRow,
  enableSearch = true,
  tableProps,
  paginationDependencies,
  filterPredicate,
  useLocalPagination = false,
}: SimpleTableProps<T>) {
  const [searchTerm, setSearchTerm] = useState('');

  const autoFilterPredicate = useMemo(() => {
    if (filterPredicate) {
      return filterPredicate;
    }

    const searchableAccessors =
      searchAttributes ??
      columns
        .filter(col => col.accessor && col.accessor !== 'actions')
        .map(col => col.accessor as keyof T);

    return (item: T, search: string): boolean => {
      if (!search.trim()) return true;

      const searchLower = search.toLowerCase();

      return searchableAccessors.some(accessor => {
        const value = item[accessor];

        if (typeof value === 'string') {
          return value.toLowerCase().includes(searchLower);
        }

        if (typeof value === 'number') {
          return value.toString().includes(search);
        }

        if (value && typeof value === 'object' && 'toLocaleDateString' in value) {
          return (value as Date).toLocaleDateString().toLowerCase().includes(searchLower);
        }

        if (typeof value === 'object' && value !== null) {
          try {
            return JSON.stringify(value).toLowerCase().includes(searchLower);
          } catch {
            return false;
          }
        }

        return String(value).toLowerCase().includes(searchLower);
      });
    };
  }, [columns, filterPredicate]);

  const showSearch = mode === 'client' && enableSearch;
  const crudFilters = useMemo(() => {
    if (!filters) return [];
    return Object.entries(filters).map(([field, value]) => ({
      field,
      operator: 'eq' as const,
      value,
    }));
  }, [filters]);

  const tableData = useTable<T>({
    resource,
    mode,
    columns,
    searchTerm: showSearch ? searchTerm : undefined,
    filterPredicate: showSearch ? autoFilterPredicate : undefined,
    paginationDependencies,
    filters: crudFilters,
    pageSize,
    useLocalPagination,
  });

  return (
    <Table<T>
      title={title}
      titleIcon={titleIcon}
      onTitleClick={onTitleClick}
      columns={columns}
      data={tableData.data}
      isLoading={tableData.isLoading}
      onRowClick={onRowClick || (() => {})}
      searchTerm={showSearch ? searchTerm : undefined}
      setSearchTerm={showSearch ? setSearchTerm : undefined}
      pagination={tableData.pagination}
      totalRecords={tableData.totalRecords}
      sortStatus={tableData.sortStatus}
      onSortStatusChange={tableData.onSortStatusChange}
      primaryButton={primaryButton}
      HeaderRow={HeaderRow}
      showCount={showCount}
      {...tableProps}
    />
  );
}
